/**
 * Dismissing a block, and restoring it (section 9.4; ADR 0058). A dismissed
 * block is kept, hidden from the board unless "Show dismissed" is on.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useLayoutEffect, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import type { DismissReason } from "../../api/types";
import { useSession } from "../../app/session-context";
import { type BlockView, DISMISS_REASONS, dismissReasonLabel } from "./model";

const inputClass = "w-full rounded-input border border-line bg-card px-3 text-[13.5px] text-text";
const secondary =
  "h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-50";
const NOTE_LIMIT = 500;

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return "This block can't change any more. Reload to see where it is now.";
    }
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    return error.fieldErrors[0]?.message ?? error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

/**
 * The reason and an optional note: the popup's Dismiss ▾ → Other… opens it,
 * on Other with the note focused (ADR 0194); a reason alone is one click in
 * that menu.
 */
export function DismissBlock({
  block,
  onDone,
  onCancel,
  initialReason,
}: {
  block: BlockView;
  /** Told what happened, once the day shows it (#247). */
  onDone: (message: string) => void;
  /** Cancel: back to what the popup showed before (ADR 0193). */
  onCancel: () => void;
  /** Chosen already (Other…): the note takes focus. */
  initialReason?: DismissReason | undefined;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const reasonId = useId();
  const noteId = useId();
  const [reason, setReason] = useState<DismissReason | "">(initialReason ?? "");
  const noteField = useRef<HTMLTextAreaElement>(null);
  const [startOnNote] = useState(initialReason !== undefined);
  useLayoutEffect(() => {
    if (startOnNote) {
      noteField.current?.focus();
    }
  }, [startOnNote]);
  const [note, setNote] = useState("");
  const dismiss = useMutation({
    mutationFn: (chosen: DismissReason) => api.dismissBlock(block.id, chosen, note.trim() || null),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      onDone(`Dismissed ${block.title}.`);
    },
  });

  return (
    <form
      aria-label="Dismiss this block"
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (reason !== "") {
          dismiss.mutate(reason);
        }
      }}
    >
      <div className="flex flex-col gap-[5px]">
        <label htmlFor={reasonId} className="text-xs font-semibold text-muted">
          Reason
        </label>
        <select
          id={reasonId}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value as DismissReason | "");
          }}
          className={`${inputClass} h-10 px-2`}
        >
          <option value="">Choose a reason</option>
          {DISMISS_REASONS.map((r) => (
            <option key={r.reason} value={r.reason}>
              {r.label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-[5px]">
        <label htmlFor={noteId} className="text-xs font-semibold text-muted">
          Note (optional)
        </label>
        <textarea
          ref={noteField}
          id={noteId}
          value={note}
          maxLength={NOTE_LIMIT}
          rows={2}
          onChange={(event) => {
            setNote(event.target.value);
          }}
          className={`${inputClass} py-2`}
        />
      </div>
      {dismiss.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(dismiss.error)}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={reason === "" || dismiss.isPending}
          className="h-10 cursor-pointer rounded-pill bg-cta-bg px-5 text-[13.5px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
        >
          {dismiss.isPending ? "Dismissing…" : "Dismiss block"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className={secondary}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Why a dismissed block was dismissed, and "Restore". */
export function DismissedBlock({
  block,
  onDone,
}: {
  block: BlockView;
  /** Told what happened, once the day shows it (#247). */
  onDone: (message: string) => void;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const restore = useMutation({
    mutationFn: () => api.restoreBlock(block.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      onDone(`Restored ${block.title}.`);
    },
  });
  const why =
    block.dismissReason === "already_logged" && block.coveredBy !== null
      ? block.coveredBy
      : `Dismissed: ${dismissReasonLabel(block.dismissReason)}.`;

  return (
    <section aria-label="Dismissed" className="flex flex-col gap-2.5 rounded-row border border-dashed border-line p-3">
      <p className="text-[13px]">{why}</p>
      {block.dismissNote ? <p className="text-[13px] text-muted">“{block.dismissNote}”</p> : null}
      {restore.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(restore.error)}
        </p>
      ) : null}
      <button
        type="button"
        disabled={restore.isPending}
        onClick={() => {
          restore.mutate();
        }}
        className={secondary}
      >
        {restore.isPending ? "Restoring…" : "Restore"}
      </button>
    </section>
  );
}
